import { Injectable } from '@angular/core';
import Daily, { DailyCall } from '@daily-co/daily-js';
import { DeviceSettingsService } from '../device-settings.service';
import { PlayerService } from '../player.service';
import { ControlService } from '../control.service';
import { CallService } from 'src/app/services/learner/call.service';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { UserVideoService } from '../user-video.service';
import { AppConstants } from 'src/app/app-constants';
import { IGuidedInfo } from 'src/app/interfaces/iguided-info';
import { IGuidedClip } from 'src/app/interfaces/iguided-clip';
import { AssemblyService } from '../assembly/assembly.service';
import { CallStoreService } from '../call-store/call-store.service';
import { MediaStreamService } from '../media-stream/media-stream.service';
import {
  replaceFromWithTo,
  replaceToWithFrom,
} from 'src/app/utils/string-replacements';
import {
  correctedTranscript,
  correctTranscript,
} from 'src/app/utils/transcript-corrector';
import { TranslocoService } from '@jsverse/transloco';
import { ICallVisualAid } from 'src/app/interfaces/icall-visual-aid';
import { effect } from '@angular/core';
import { IPreloadedImageResult } from 'src/app/interfaces/ipreloaded-image-result';
import { ImagePreloaderService } from 'src/app/services/learner/image-preloader/image-preloader.service';
import { CallValidationService } from '../call-validation/call-validation.service';
import { IAgentInteractionBlockRequest } from 'src/app/interfaces/call/conversational/iagent-interaction-block-request';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SentryService } from 'src/app/services/logger/sentry.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';

@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  deviceSettingsService: DeviceSettingsService | undefined;
  playerService: PlayerService | undefined;
  controlService: ControlService | undefined;

  conversationUrl: string = '';
  conversationId: string = '';
  dailyCall: DailyCall | null = null;

  replicaIdDaily: string = '';
  userIdDaily: string = '';
  participantAgent: string = 'Agent';
  participantUser: string = 'User';
  sendPayload: boolean = false;
  timeoutAvatarSpeaking: any;

  lastMessageTime: Date = new Date();
  inactivityAlertTitle: string = 'This call will end in 60 seconds';
  inactivityAlertMessage: string =
    'This call is being ended due to inactivity. Speak to the avatar to continue or select "End Call" to end the call.';
  isFirstMessage: boolean = true;
  isUserFirstMessage: boolean = true;
  avatarInteractionCount: number = 0;
  avatarLastMessage: string = '';
  idleInterval: any;
  keepAliveInterval: any;
  pauseInterval: any;
  LONG_PAUSE_THRESHOLD: number = 60; // 60 seconds before the call is terminated on long pause
  pauseCountdown: number = this.LONG_PAUSE_THRESHOLD;
  resumeMessage: string[] = [];
  isLongPause: boolean = false;
  isTeminateConversationInProgress: boolean = false;
  hasJoinedSession: boolean = false;
  isAvatarVideoReady: boolean = false;
  isResumeMessagePending: boolean = false;
  isResumeMessageInProgress: boolean = false;
  isGuidedCall: boolean = false;
  guidedInfo: IGuidedInfo | null = null;
  guidedClips: IGuidedClip[] = [];
  isFirstGuidedInteraction: boolean = false;
  guidedIndex: number = 0;

  lastQuestionUuidSent: string | null = null;
  lastAvatarSpeech: string = '';
  triggerEndCallAfterAnswer: boolean = false;
  isStartCallInProgress: boolean = false;

  // handle resume
  wasAvatarSpeaking: boolean = false;
  avatarResumeMessage: string = '';
  avatarResumeAccumulated: string = '';
  triggerResumeMessage: boolean = false;
  joinedMeeting: boolean = false;
  customBackgroundUrl?: string;
  chromaKeyAnimationFrameId: number | null = null;
  triggerStartTranscriber: boolean = false;

  visualAidObject: ICallVisualAid | undefined;
  private shouldConnectTranscriberOnMicOn: boolean = false;
  conversationInitializedByPathwayFlow: boolean = false;

  // Observable for welcome message completion (intro/preamble calls)
  private welcomeMessageSpokenSubject = new Subject<void>();
  welcomeMessageSpoken$ = this.welcomeMessageSpokenSubject.asObservable();

  // Override called when a guided call auto-completes (continueHidden && isCallComplete).
  // If set, replaces the default endRealtimeConversation() call. Used by quiz pathway steps.
  onAutoComplete: (() => void) | null = null;

  private readonly _error$ = new Subject<Error>();
  public get onError$() {
    return this._error$.asObservable();
  }

  constructor(
    private callService: CallService,
    private userVideoService: UserVideoService,
    private callStore: CallStoreService,
    private assemblyService: AssemblyService,
    private translocoService: TranslocoService,
    private imagePreloaderService: ImagePreloaderService,
    private callValidationService: CallValidationService,
    private mediaStreamService: MediaStreamService,
    private logger: LoggerService,
    private router: Router,
    private sentryService: SentryService,
  ) {
    effect(
      () => {
        const userSpokeValue = this.callStore.getUserSpoke();
        this.callStore.setLastMessageTimeStamp(new Date());
      },
      { allowSignalWrites: true },
    );
    console.log('RealtimeService: Effect setup complete');
  }

  setDeviceSettingsService(deviceSettingsService: DeviceSettingsService): void {
    this.deviceSettingsService = deviceSettingsService;
  }

  setPlayerService(playerService: PlayerService): void {
    this.playerService = playerService;
  }

  setControlService(controlService: ControlService): void {
    this.controlService = controlService;
  }

  async handleCallEvents(): Promise<void> {
    if (this.dailyCall) {
      this.logger.log('[CALL] tavus - Destroying existing DailyCall instance');
      await this.dailyCall.destroy();
      this.dailyCall = null;
    }

    if (!this.conversationUrl) {
      this.logger.error('[CALL] tavus - No conversation URL found');
      return;
    }

    this.logger.log(`[CALL] tavus - Starting realtime conversation`);
    this.dailyCall = Daily.createCallObject();
    this.hasJoinedSession = false;
    this.joinedMeeting = false;
    this.isAvatarVideoReady = false;
    this.isResumeMessagePending = false;

    // Handle call events
    this.dailyCall.on('joined-meeting', this.handleJoinedMeeting.bind(this));
    this.dailyCall.on(
      'participant-joined',
      this.handleParticipantJoinedOrUpdated.bind(this),
    );
    this.dailyCall.on(
      'participant-left',
      this.handleParticipantLeft.bind(this),
    );
    this.dailyCall.on(
      'participant-updated',
      this.handleParticipantJoinedOrUpdated.bind(this),
    );
    this.dailyCall.on('error', this.handleError.bind(this));
    this.dailyCall.on('app-message', this.handleAppMessage.bind(this));
    this.dailyCall.on(
      'transcription-message',
      this.handleTranscriptionMessage.bind(this),
    );
    // Monitor network connection status and quality changes
    this.dailyCall.on('network-connection', (event) => {
      if (event.event === 'disconnected' || event.event === 'interrupted') {
        this.logger.error(`[CALL] tavus - network connection ${event.event}`, {
          event,
        });
        return;
      }
      this.logger.log('[CALL] tavus - network-connection', event);
    });
    this.dailyCall.on('network-quality-change', (event: any) => {
      if (event.threshold === 'very-low') {
        this.logger.error('[CALL] tavus - network quality very-low', {
          event,
        });
        return;
      }
      this.logger.log('[CALL] tavus - network-quality-change', event);
    });

    // Prevent audio if resume is in progress
    const isResume =
      this.controlService?.isResumeInProgress ||
      this.controlService?.isResumeMessageInProgress;
    this.logger.log(
      `[CALL] tavus - handleCallEvents is resume in progress ${isResume}`,
    );

    this.dailyCall
      .join({ url: this.conversationUrl, audioSource: !isResume })
      ?.then(() => {
        this.logger.log('[CALL] tavus - Joined call');
      })
      .catch((err) => {
        this.logger.error('[CALL] tavus - Error joining call:', err);
      });
  }

  handleJoinedMeeting(event: any): void {
    console.log('TAVUS ::: handle joined meeting:', event);
    this.joinedMeeting = true;

    // Local tracks
    const tracks = this.dailyCall?.participants().local.tracks;
    console.log('TAVUS ::: Local tracks:', tracks);

    // Display user video
    this.userVideoService.displayUserVideo();

    // Get and set devices
    const videoDeviceId =
      this.deviceSettingsService?.getVideoDeviceLocalStorage() || false;
    const audioDeviceId =
      this.deviceSettingsService?.getAudioDeviceLocalStorage() || false;
    this.setCallDevices(videoDeviceId, audioDeviceId);

    const isGuidedCall = this.isGuided();
    if (!isGuidedCall) {
      this.hasJoinedSession = true;
    }

    // start user decibel detection
    this.deviceSettingsService?.startAudioLevelDetection(
      audioDeviceId || 'default',
    );

    // Set mic off to prevent audio filtering
    this.toggleMic(false);
  }

  async setCallDevices(
    videoDeviceId: string | false | null,
    audioDeviceId: string | false | null,
  ): Promise<void> {
    if (!videoDeviceId && !audioDeviceId) {
      return this.logger.error('[CALL] tavus - No devices provided');
    }

    const items = {
      ...(videoDeviceId && { videoDeviceId }),
      ...(audioDeviceId && { audioDeviceId }),
    };
    try {
      const resp = await this.dailyCall?.setInputDevicesAsync(items);
      this.logger.log('[CALL] tavus - Set call devices successfully', resp);
    } catch (err) {
      this.logger.error('[CALL] tavus - Error setting call devices:', err);
    }
  }

  handleParticipantJoinedOrUpdated(event: any): void {
    const { participant, action } = event;
    const { local, session_id: participantId, tracks } = participant;
    const eventAction = action.split('-')[1];
    this.handleParticipantId(local, participantId);

    console.log(`TAVUS ::: Participant ${eventAction} (local: ${local})`, {
      event,
    });

    if (local) return;

    Object.entries(tracks).forEach(([trackType, trackInfo]) => {
      if ((trackInfo as any).persistentTrack) {
        this.updateParticipantMediaTrack(trackType, trackInfo, participantId);
      }
    });

    if (eventAction !== 'joined') return;
    setTimeout(() => {
      // Handle avatar joined
      this.handleGuidedFirstInteraction();
      this.triggerAvatarResumeMessage();
    }, 2000);
  }

  /**
   * Handles participant ID assignment for Daily.co call participants.
   * Stores the session ID for either local user or remote avatar/replica.
   *
   * @param local - Boolean indicating if the participant is the local user
   * @param participantId - The Daily.co session ID of the participant
   */
  handleParticipantId(local: boolean, participantId: string): void {
    // Validate participantId is provided and valid
    if (
      !participantId ||
      typeof participantId !== 'string' ||
      participantId.trim() === ''
    ) {
      this.logger.error(`[CALL] tavus - Invalid participant ID received`);
      return;
    }

    if (local) {
      // Prevent overwriting existing user ID unless it's different
      if (this.userIdDaily && this.userIdDaily === participantId) return;
      this.userIdDaily = participantId;

      // Log user joined
      this.logger.log('[CALL] tavus - user joined call');
    } else {
      // Prevent overwriting existing replica ID unless it's different
      if (this.replicaIdDaily && this.replicaIdDaily === participantId) return;

      this.replicaIdDaily = participantId;

      // Log replica joined
      this.logger.log('[CALL] tavus - replica joined call');
    }
  }

  updateParticipantMediaTrack(
    trackType: string,
    track: unknown,
    participantId: any,
  ) {
    console.log(`TAVUS ::: Manage avatar track...`, { track });
    const id =
      trackType === 'video' ? 'avatar-video-realtime' : 'avatar-audio-realtime';

    const trackEl =
      trackType === 'video'
        ? (document.getElementById(id) as HTMLVideoElement)
        : (document.getElementById(id) as HTMLAudioElement);

    if (!trackEl) {
      this.logger.error(
        `[CALL] tavus - ${trackType} element does not exist for participant: ${participantId}`,
      );
      return;
    }

    // Check if track is already playing
    const trackSrcObject = (trackEl as HTMLMediaElement)
      .srcObject as MediaStream | null;
    const existingTracks = trackSrcObject?.getTracks();

    // Check if track needs to be updated or added
    const needsUpdate = !existingTracks?.includes(
      (track as any).persistentTrack,
    );

    if (needsUpdate) {
      try {
        trackEl.srcObject = new MediaStream([(track as any).persistentTrack]);
        trackEl.onloadedmetadata = () => {
          trackEl
            .play()
            .catch((e) =>
              this.logger.error(
                `[CALL] tavus - Error playing ${trackType} for participant ${participantId}:`,
                e,
              ),
            );
        };
      } catch (error) {
        throw new Error('Error playing the user video on realtime service');
      }
    }

    const isTrackPlayable = (track as any).state === 'playable';
    if (trackType === 'video' && isTrackPlayable) {
      this.isAvatarVideoReady = true;
      // Trigger the resume message if it is pending when avatar video is ready
      if (this.isResumeMessagePending) {
        this.isResumeMessagePending = false;
        this.triggerAvatarResumeMessage();
      }
    }
  }

  handleParticipantLeft(event: any): void {
    console.log('TAVUS ::: Participant left:', event);
  }

  handleError(event: any): void {
    const errorMsg = event.errorMsg || 'Unknown error';
    const isMeetingEnded = errorMsg.includes('Meeting has ended');
    this.logger.error('[CALL] tavus - error', {
      isMeetingEnded,
      message: errorMsg,
    });
    if (!this.controlService) return;

    // if conversation is paused we don't end the call
    if (this.callStore.getIsPaused()) return;

    // if conversation is terminating in progress we don't end the call
    if (this.isTeminateConversationInProgress) return;

    // if resume is in progress we don't end the call
    if (this.controlService.isResumeInProgress) return;

    this.logger.error('[CALL] tavus - Daily call error:', event);
    if (isMeetingEnded) {
      this.logger.error(
        '[CALL] tavus - The meeting has ended due to an error. Triggering the end conversation endpoint.',
      );
      this.endRealtimeConversation(true);
    }
  }

  handleAppMessage(event: any): void {
    if ('data' in event && this.playerService) {
      const data = event.data;
      const { message_type, event_type } = data;
      if (event_type === undefined) return;

      const isConversation = message_type === 'conversation';
      if (isConversation) {
        switch (event_type) {
          case 'conversation.utterance':
            this.handleConversation(data);
            break;
          case 'conversation.replica.started_speaking':
            this.wasAvatarSpeaking = true;
            this.hasJoinedSession = true;
            this.callStore.setIsAvatarThinking(false);
            this.callStore.setIsAvatarSpeaking(true);
            this.callStore.setIsContinueButtonBlocked(true);
            this.handleConversationStarted();

            // Log replica started speaking
            this.logger.log('[CALL] tavus - replica start speaking');
            break;
          case 'conversation.replica.stopped_speaking':
            this.callStore.setIsAvatarSpeaking(false);
            this.startIdleTimer();
            this.startKeepAliveTimer();
            this.handleAvatarStoppedSpeaking();

            // Log replica stopped speaking
            this.logger.log('[CALL] tavus - replica stop speaking');
            break;
          case 'conversation.user.started_speaking':
            this.stopIdleTimer();

            // Log user started speaking
            this.logger.log('[CALL] tavus - user start speaking');
            break;
          case 'conversation.user.stopped_speaking':
            // In case of pause while user is speaking, we send the interrupt event to prevent the avatar responding
            if (this.callStore.getIsPaused()) {
              this.sendConversationInterruptEvent();
              return;
            }
            this.handleInteraction();

            // Log user stopped speaking
            this.logger.log('[CALL] tavus - user stop speaking');
            break;
          default:
            this.logger.log(`[CALL] tavus - event_type: ${event_type}`, {
              event,
            });
            break;
        }
      } else {
        this.logger.log(`[CALL] tavus - event_type: ${event_type}`, { event });
      }
    }
  }

  handleGuidedFirstInteraction(): void {
    if (!this.isFirstGuidedInteraction) return;
    this.isFirstGuidedInteraction = false;

    // Display pause and continue buttons
    this.callStore.setIsContinueButtonHidden(false);
    this.callStore.setIsContinueButtonBlocked(true);
    this.hasJoinedSession = true;

    const displayTour = this.shouldDisplayTour();
    if (displayTour && this.controlService) {
      this.controlService.displayTour = true;
      return;
    }

    console.log(`TAVUS ::: First guided interaction, sending echo message`);
    this.handleGuidedEchoMessage();
  }

  triggerAvatarResumeMessage(): void {
    if (!this.triggerResumeMessage) return;
    if (!this.avatarResumeMessage) return;
    if (this.avatarResumeMessage.trim().length === 0) return;
    if (!this.isAvatarVideoReady) {
      console.log(
        `TAVUS ::: Avatar is not present, cannot trigger resume message yet`,
      );
      this.isResumeMessagePending = true;
      return;
    }

    // Trigger the avatar echo message if it exists
    console.log(`TAVUS ::: send echo: ${this.avatarResumeMessage}`);
    this.triggerResumeMessage = false;
    this.isResumeMessageInProgress = true;
    this.sendAvatarEchoMessage(this.avatarResumeMessage);
  }

  handleGuidedEchoMessage(): void {
    const index = this.guidedIndex;
    if (this.guidedClips.length <= index) {
      this.logger.error(
        `[CALL] tavus - Index [${index}] is out of bounds for guided clips`,
        this.guidedClips,
      );
      return;
    }

    const clip = this.guidedClips[this.guidedIndex];
    const hint = clip.hints;
    this.callStore.setHints(hint || '');

    if (!clip) {
      this.logger.error(
        `[CALL] tavus - No guided clip found at index ${index}`,
      );
      return;
    }

    const { agentSays } = clip;

    if (!agentSays?.trim()) {
      this.lastAvatarSpeech = '';
      if (index === 0) {
        // If first clip has no agentSays, we unblock the call
        this.wasAvatarSpeaking = false;
        this.hasJoinedSession = true;
        this.callStore.setIsAvatarThinking(false);
        this.callStore.setIsAvatarSpeaking(false);
        this.handleConversationStarted();
        this.startIdleTimer();
        this.handleAvatarStoppedSpeaking();
      }
      console.warn(`TAVUS ::: No agentSays content at index ${index}`);
      return;
    }

    console.log(`TAVUS ::: send echo: ${agentSays}`);
    this.lastAvatarSpeech = agentSays;
    this.sendAvatarEchoMessage(agentSays);

    if (!this.callStore.getIsSendingAnswer()) {
      this.addChatHistory('agentSays', agentSays);
    }
  }

  async handleClickContinue(): Promise<void> {
    if (!this.isGuidedCall) return;
    const questionUuid = this.guidedClips[this.guidedIndex].questionUuid || '';
    this.guidedIndex = this.guidedIndex + 1;
    const visualAidIndex =
      this.guidedClips[this.guidedIndex].visualAidIndex || 0;

    this.logger.log('[CALL] tavus - guided continue/next button click', {
      questionUuid: questionUuid,
      visualAidIndex: visualAidIndex,
    });

    // Stop keep-alive timer when user progresses through guided call
    this.stopKeepAliveTimer();

    const image = this.imagePreloaderService.getImageSrcByIndex(visualAidIndex);
    if (image) {
      console.log('Set visual aid image URL:', image);
      this.callStore.setVisualAidImageUrl(image);
    }
    console.log('TAVUS ::: Blocking continue button in handleClickContinue');
    this.callStore.setIsContinueButtonBlocked(true);
    console.log('Realtime: Setting isSendingAnswer to true');
    this.callStore.setIsSendingAnswer(true);
    this.handleGuidedEchoMessage();
    const time = this.controlService?.isVisualAidCall ? 2500 : 900;
    await new Promise<void>((resolve) => setTimeout(resolve, time));
    await this.processUserResponse(questionUuid);
    this.verifyLastQuestionInGuidedCall();
  }

  async processUserResponse(questionUuid: string): Promise<void> {
    const avatarSpeech = this.lastAvatarSpeech;
    this.logger.log('[CALL] tavus - Guided call, processing user response', {
      questionUuid,
    });

    // Set processing state to prevent Assembly Service from unblocking button
    if (this.controlService?.isVisualAidCall) {
      this.callStore.setIsProcessingAnswer(true);
    }

    // Force end the current utterance
    this.assemblyService.forceEndUtterance();
    this.assemblyService.pause();

    // Get partial transcript and check if it is already recognized
    let partialTranscript = this.callStore.getSpeechTranscriptPartial();
    const includePartial = this.callStore
      .getRecognizedTranscripts()
      .includes(partialTranscript);
    if (!includePartial && partialTranscript.length > 0) {
      // Handle call words and replace words
      const callWords = this.callStore.getCallWords();
      const replaceWords = this.callStore.getReplaceWords();
      if (callWords.length > 0 && replaceWords.length > 0) {
        const correctedPartialTranscript: correctedTranscript =
          correctTranscript(partialTranscript, callWords, replaceWords);
        this.logger.log('[CALL] tavus - conversation speech', {
          originalText: partialTranscript,
          correctedText: correctedPartialTranscript.transcript,
        });
        // Update the partial transcript with the corrected one
        partialTranscript = correctedPartialTranscript.transcript;
        this.callStore.addTranscriptChangesLogs(
          correctedPartialTranscript.changes,
        );
      }
      // Add the partial transcript to the recognized transcripts
      this.callStore.setRecognizedTranscripts([
        ...this.callStore.getRecognizedTranscripts(),
        partialTranscript,
      ]);
    }

    // Get the full transcript
    const transcript = this.callStore.getRecognizedTranscripts().join(' ');
    this.clearTranscripts();
    this.handleAvatarEmptySpeech();

    if (transcript.length === 0) {
      this.logger.error('[CALL] tavus - No user speech transcript to send');
      // Clear processing state if no transcript
      if (this.controlService?.isVisualAidCall) {
        this.callStore.setIsProcessingAnswer(false);
      }
      return;
    }

    // Save chat history, here when user transcript is ready
    this.addChatHistory('userSays', transcript);

    // If it's a visual aid call, we allow empty avatar speech
    const allowEmptySpeech = this.controlService?.isVisualAidCall;
    this.addChatHistory('agentSays', avatarSpeech, allowEmptySpeech);

    // send user response
    if (questionUuid) {
      console.log(
        'TAVUS ::: Sending answer to server, continue button will be managed by sendAnswer',
      );
      await this.sendAnswer(transcript, questionUuid);
    }

    // Clear processing state after completion
    if (this.controlService?.isVisualAidCall) {
      this.callStore.setIsProcessingAnswer(false);
    }
  }

  private clearTranscripts(): void {
    this.callStore.setSpeechTranscript('');
    this.callStore.setRecognizedTranscripts([]);
    this.callStore.setSpeechTranscriptPartial('');
  }
  isGuided(): boolean {
    return (
      this.controlService?.callType === AppConstants.CALL_TYPE_GUIDED
    );
  }

  isFraming(): boolean {
    return (
      this.controlService?.callType === AppConstants.CALL_TYPE_FRAMING
    );
  }

  verifyLastQuestionInGuidedCall(): void {
    const isGuidedCall = this.isGuided();
    if (!isGuidedCall) return;
    // Validate interaction count
    const interactions = this.controlService?.interactionCount || 0;
    const required = this.controlService?.interactionRequirement || 0;
    if (interactions < required) {
      console.log(
        `TAVUS ::: Interaction count ${interactions} is less than required ${required}, not hiding continue button`,
      );
      this.callStore.setIsCallComplete(false);
      // Resume assembly audio transcription if conditions are met
      const isMicActive =
        !this.callStore.getIsPaused() && this.callStore.getIsMicOn();
      const assemblyState = this.assemblyService.getAudioContextState();
      const isAssemblyNotRunning = assemblyState != 'running';
      const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();
      if (isAssemblyNotRunning && isMicActive && !isAvatarSpeaking) {
        this.logger.log('[CALL] tavus - Resuming assembly audio transcription');
        this.assemblyService.resume();
      }
      return;
    }

    // if is last index hide continue and pause buttons
    if (this.guidedIndex >= this.guidedClips.length - 1) {
      this.callStore.setIsContinueButtonHidden(true);
      this.hasJoinedSession = false;
      console.log(
        `TAVUS ::: Last guided clip reached, hiding continue & pause buttons`,
      );
    }

    // Check if any of the remaining guided clips contain a questionUuid
    const isNextQuestionAvailable = this.hasNextQuestion();
    console.log(
      `TAVUS ::: Next question available: ${isNextQuestionAvailable}`,
    );

    // if there is no questionUuid on remaining guided clips, we mark the call as complete
    this.callStore.setIsCallComplete(!isNextQuestionAvailable);
    console.log(`TAVUS ::: Guided call complete: ${!isNextQuestionAvailable}`);
  }

  hasNextQuestion(): boolean {
    // Validate if array contains index plus one
    if (this.guidedIndex >= this.guidedClips.length - 1) return false;
    // validate if on remaining guided clips there is a questionUuid
    const remainingClips = this.guidedClips.slice(this.guidedIndex + 1);
    const hasQuestionUuid = remainingClips.some(
      (clip) => clip.questionUuid != null && clip.questionUuid.trim() !== '',
    );
    return hasQuestionUuid;
  }

  sendAnswer(answer: string, questionUuid: string): Promise<void> {
    const practiceCallUuid = this.callStore.getCall()?.practiceCallUuid;
    // const questionUuid = this.guidedClips[index].questionUuid;
    if (!practiceCallUuid) {
      throw new Error('practiceCallUuid is null');
    }
    if (!questionUuid) {
      throw new Error('questionUuid is null');
    }
    if (!answer) {
      throw new Error(`empty answer for questionUuid [${questionUuid}]`);
    }

    const shouldSend =
      questionUuid != null &&
      answer.length > 0 &&
      questionUuid != this.lastQuestionUuidSent;

    if (shouldSend) {
      console.log(
        `SEND answer to server \n questionUuid [${questionUuid}]\n practiceCallUuid [${this.controlService?.practiceCallUuid}]\n answer %c[${answer}]`,
        'background: #00647B; color: #fff;',
      );
      this.logger.log('[CALL] tavus - guided send answer', {
        questionUuid: questionUuid,
        answer: answer,
      });
      // we save the last questionUuid sent to prevent sending the same questionUuid twice
      this.lastQuestionUuidSent = questionUuid;

      this.callStore.setAnswerData(null);
      this.callStore.setWasSendingAnswer(false);

      return new Promise<void>((resolve, reject) => {
        this.callService
          .sendUserAnswer(practiceCallUuid, questionUuid, answer)
          .subscribe({
            next: () => {
              this.controlService?.addInteraction();
            },
            error: (err) => {
              console.error(err);
              this.logger.error(
                '[CALL] tavus - guided send answer error',
                err,
                {
                  questionUuid: questionUuid,
                  answer: answer,
                },
              );
              // if offline on guided call, save the answer to send later
              const isOnline = navigator.onLine;
              console.log(`Internet connected - is on line? ${isOnline}`);
              if (!isOnline) {
                this.callStore.setWasSendingAnswer(true);
                this.callStore.setAnswerData({
                  practiceCallUuid,
                  questionUuid,
                  answer,
                });
                this.logger.error(
                  `[CALL] tavus - Error sending answer, you are offline!`,
                );
              }
              // Clear processing state on error
              if (this.controlService?.isVisualAidCall) {
                this.callStore.setIsProcessingAnswer(false);
              }
              reject(err);
            },
            complete: () => {
              console.log(`sendUserAnswer to questionUuid [${questionUuid}]`);
              console.log(
                `TAVUS ::: Blocking continue button in sendUserAnswer complete`,
              );
              this.callStore.setIsContinueButtonBlocked(true);
              console.log('Realtime: Setting isSendingAnswer to false');
              this.callStore.setIsSendingAnswer(false);
              this.verifyLastQuestionInGuidedCall();
              if (this.triggerEndCallAfterAnswer) {
                console.log(
                  `TAVUS ::: Triggering end call after answer for questionUuid [${questionUuid}]`,
                );
                this.endRealtimeConversation();
              }
              // Clear processing state on completion
              if (this.controlService?.isVisualAidCall) {
                this.callStore.setIsProcessingAnswer(false);
              }
              resolve();
            },
          });
      });
    } else {
      console.log(
        `TAVUS ::: sending answer unblocking continue button [${questionUuid}]`,
      );
      this.callStore.setIsContinueButtonBlocked(false);
      // Clear processing state if not sending
      if (this.controlService?.isVisualAidCall) {
        this.callStore.setIsProcessingAnswer(false);
      }
      return Promise.resolve();
    }
  }

  handleInteraction(): void {
    if (this.isFirstMessage) return;
    this.controlService?.addInteraction();
    console.log(
      `TAVUS ::: Interaction count: ${this.controlService?.interactionCount}`,
    );
  }

  handleConversationStarted(): void {
    if (this.controlService?.isResumeInProgress) {
      this.toggleUserMediaStream(true);
      this.controlService.isResumeInProgress = false;
      this.callStore.setIsPaused(false);
    }
    if (!this.isFirstMessage) return;
    // User time indicator
    this.controlService?.startTimer();
  }

  async handleAvatarStoppedSpeaking(): Promise<void> {
    if (!this.controlService) return;
    const isPaused = this.callStore.getIsPaused();
    if (!isPaused) {
      this.wasAvatarSpeaking = false;
    }
    // On guided calls, we resume assembly audio sst
    if (this.isGuidedCall) {
      // Only unblock continue button if not processing an answer
      if (
        !this.controlService?.isVisualAidCall ||
        !this.callStore.getIsProcessingAnswer()
      ) {
        console.log(
          'TAVUS ::: Unblocking continue button in handleAvatarStoppedSpeaking',
        );
        this.callStore.setIsContinueButtonBlocked(false);
      } else {
        console.log(
          'TAVUS ::: Continue button remains blocked - answer processing in progress',
        );
      }
      this.callStore.setIsVisualAidOpen(true);
      const continueHidden = this.callStore.getIsContinueButtonHidden();
      const isCallComplete = this.callStore.getIsCallComplete();
      if (continueHidden && isCallComplete) {
        if (this.onAutoComplete) {
          console.log(
            `TAVUS ::: Guided all complete, delegating to onAutoComplete`,
          );
          this.onAutoComplete();
        } else {
          console.log(
            `TAVUS ::: Guided all complete, auto ending conversation`,
          );
          // Save the last avatar speech
          this.endRealtimeConversation();
        }
      }
      if (!isPaused && !this.triggerStartTranscriber) {
        const isMicOn = this.callStore.getIsMicOn();
        if (isMicOn) {
          this.assemblyService.resume();
        }
      }
      this.triggerStartTranscriber = false;
    }
    // When the avatar finishes speaking the resume message, re-enable the user's microphone
    const isResume = this.controlService.isResumeMessageInProgress;
    if (isResume) {
      this.controlService.isResumeMessageInProgress = false;
      this.toggleMic(this.controlService.isMicOnBeforePause);

      // handle call paused on first message before the avatar speaks
      if (this.isFirstMessage) {
        this.isFirstMessage = false;
        this.assemblyService.connectTranscriber(true);
      }
      return;
    }

    // Check if welcome message completed for framing calls (intro/preamble)
    if (
      this.isFirstMessage &&
      this.callStore.getCallType() === AppConstants.CALL_TYPE_FRAMING
    ) {
      if (!isPaused) {
        this.welcomeMessageSpokenSubject.next();
      }
    }

    if (!this.isFirstMessage) return;
    // Only if is the first message
    this.isFirstMessage = false;
    // if call is paused, we don't activate the mic
    if (this.callStore.getIsPaused()) return;
    // When avatar stops speaking, activate mic respecting the user's explicit preference
    this.toggleMic(this.callStore.getUserMicPreference());

    if (this.isGuidedCall) {
      this.assemblyService.connectTranscriber(true);
    }
  }

  handleConversation(data: any): void {
    if (!this.playerService) return;
    if (!data) {
      this.logger.error('[CALL] tavus - No data provided');
      return;
    }

    if (!('properties' in data)) {
      this.logger.error('[CALL] tavus - No properties provided');
      return;
    }
    if (!('role' in data.properties)) {
      this.logger.error('[CALL] tavus - No role provided');
      return;
    }
    if (!('speech' in data.properties)) {
      this.logger.error('[CALL] tavus - No speech provided');
      return;
    }

    const properties = data.properties;
    const role = properties.role;
    let speech = properties.speech;

    if (speech.trim() === '') {
      console.log(`TAVUS ::: Empty (${role}) speech received, ignoring...`);
      this.triggerAvatarResumeMessage();
      return;
    }

    // if avatar speech, we clean the transcription
    const isReplica = role === 'replica';
    if (isReplica) {
      if (this.isFirstMessage) {
        this.avatarInteractionCount = 1;
      } else {
        this.avatarInteractionCount++;
      }

      this.logger.log(`[CALL] tavus - text before replaceToWithFrom`, {
        speech,
      });
      speech = this.getAvatarCorrectText(speech);
      this.logger.log(`[CALL] tavus - text after replaceToWithFrom`, {
        speech,
      });
      this.playerService.avatarSpeech.set(speech);
      // This prevents sending resume message as agentSaysFull on AIB
      this.avatarLastMessage = speech;
    }

    // Send interaction to AIB once we have at least two avatar responses
    if (this.avatarInteractionCount >= 2 && !isReplica) {
      this.handleSendInteraction();
    }

    console.log(
      `%cTAVUS ::: Message (${role}): ${speech}`,
      'background-color: #00647B; color: #fff;',
    );

    this.logger.log(`[CALL] tavus - message`, {
      role,
      speech,
    });

    const speaker = role === 'replica' ? 'agentSays' : 'userSays';

    this.addChatHistory(speaker, speech);
  }

  toggleMic(isMicOn: boolean): void {
    const isGuided = this.isGuided();
    // if mic is on, we resume assembly audio
    if (isGuided && isMicOn) {
      console.log(`TAVUS ::: Guided call, resuming assembly audio...`);
      if (this.dailyCall) {
        this.dailyCall.setLocalAudio(true);
      }
      if (this.shouldConnectTranscriberOnMicOn) {
        this.assemblyService.restartTranscriber(true);
        this.shouldConnectTranscriberOnMicOn = false;
      } else {
        this.assemblyService.resume();
      }
      return;
    }

    // if mic is off, we pause assembly audio
    if (isGuided && !isMicOn) {
      console.log(`TAVUS ::: Guided call, pausing assembly audio...`);
      this.assemblyService.pause();
      if (this.dailyCall) {
        this.dailyCall.setLocalAudio(false);
      }
      return;
    }

    // handle conversational calls with daily audio
    if (this.dailyCall && !isGuided) {
      this.dailyCall.setLocalAudio(isMicOn);
    }
  }

  startKeepAliveTimer(): void {
    // This is only required for guided calls
    if (!this.isGuidedCall) return;
    this.stopKeepAliveTimer();

    this.logger.log('[CALL] tavus - Keep-alive interval started');
    this.keepAliveInterval = setInterval(
      () => {
        const practiceCallUuid = this.callStore.getPracticeCallUuid().trim();
        this.logger.log(
          `[CALL] tavus - Keep-alive request will be sent with practiceCallUuid [${practiceCallUuid}]`,
        );
        if (practiceCallUuid) {
          this.callService.keepAlive(practiceCallUuid).subscribe({
            next: () => {
              this.logger.log(
                '[CALL] tavus - Keep-alive request sent successfully',
              );
            },
            error: (error) => {
              this.logger.error(
                '[CALL] tavus - Keep-alive request failed',
                error,
              );
            },
          });
        }
      },
      4 * 60 * 1000,
    ); // 4 minutes
  }

  stopKeepAliveTimer(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
      this.logger.log('[CALL] tavus - Keep-alive interval stopped');
    }
  }

  startIdleTimer(): void {
    this.inactivityAlertMessage = this.translocoService.translate(
      'INACTIVITY_ALERT_MESSAGE',
      {},
      'call',
    );
    // Stop any existing idle timer before starting a new one
    this.stopIdleTimer();

    const isPaused = this.callStore.getIsPaused();
    if (isPaused) {
      console.log('TAVUS ::: Call is paused, not starting idle timer');
      return;
    }

    // Start a new interval to check for user inactivity every second
    this.idleInterval = setInterval(() => {
      this.idleTimerValdation();

      const now = new Date();
      const secondsSinceLastSpeech = this.getDiffInSeconds(
        now,
        this.callStore.getLastMessageTimeStamp(),
      );

      // Thresholds (in seconds)
      const alertThreshold = 240; // Show inactivity alert after 240s (4 minutes)
      const hangupThreshold = 60; // End call after 60s of silence (1 minute)
      // Time remaining until the call will be ended
      const secondsUntilHangup =
        hangupThreshold - (secondsSinceLastSpeech - alertThreshold);

      // Determine which alert message to show
      const isFewSecondsLeft = secondsUntilHangup <= 30;
      const defaultMessage = this.translocoService.translate(
        'INACTIVITY_DEFAULT_ALERT_TITLE',
        { secondsUntilHangup },
        'call',
      );
      const shortMessage = this.translocoService.translate(
        'INACTIVITY_SHORT_ALERT_TITLE',
        {},
        'call',
      );
      this.inactivityAlertTitle = isFewSecondsLeft
        ? shortMessage
        : defaultMessage;

      // Log inactivity status every 10 seconds and on the last 5 seconds
      if (
        (secondsUntilHangup % 10 === 0 || secondsUntilHangup <= 5) &&
        secondsSinceLastSpeech > 0
      ) {
        this.logger.warn(
          `[CALL] tavus - No user speech for ${secondsSinceLastSpeech}s. Call ends in ${secondsUntilHangup}s if silence continues.`,
        );
      }

      // If user has been silent longer than hangup threshold, end the call
      if (secondsUntilHangup <= 0 && this.controlService) {
        this.logger.error(
          '[CALL] tavus - Idle time limit reached. Triggering the end conversation endpoint.',
        );
        this.endRealtimeConversation(true);
        return;
      }

      // If user is inactive but still within hangup threshold, show the alert
      if (secondsSinceLastSpeech >= alertThreshold) {
        this.callStore.setDisplayAlert(true);
        // Inform control service of user inactivity
        if (this.controlService) {
          this.controlService.userInactivity = true;
        }
      } else {
        this.callStore.setDisplayAlert(false);
      }
    }, 1000);
  }

  stopIdleTimer(): void {
    console.log('TAVUS ::: Stopping idle timer');
    // Reset alert display
    this.callStore.setDisplayAlert(false);

    // Reset the last message time to "now"
    // This is always the first thing we do when starting an idle timer
    this.callStore.setLastMessageTimeStamp(new Date());

    // Clear any existing idle interval
    clearInterval(this.idleInterval);
  }

  idleTimerValdation(): void {
    if (!this.playerService) return;

    const isNotInPracticeMode =
      this.callStore.getCurrentView() !== AppConstants.VIEW_PRACTICE;
    if (isNotInPracticeMode) {
      console.log('TAVUS ::: Not in practice mode, stopping the idle timer');
      this.stopIdleTimer();
      return;
    }
  }

  getDiffInSeconds(date1: Date, date2: Date): number {
    const diffInMs = date1.getTime() - date2.getTime();
    return Math.round(diffInMs / 1000);
  }

  async startCallRequest(doGuidedValidation: boolean = true): Promise<void> {
    if (!this.controlService) return;
    if (!this.controlService.moduleAssignmentUuid) {
      return;
    }
    if (!this.controlService.callUuid) {
      return;
    }
    if (!this.controlService.practiceType) {
      return;
    }
    if (!this.controlService.callType) {
      return;
    }

    this.conversationInitializedByPathwayFlow = false;

    this.controlService.isCaptionsOn = false;
    this.callStore.setDisplayPostCall(false);
    this.callStore.setIsContinueButtonBlocked(true);
    this.callStore.setCurrentView(AppConstants.VIEW_PRACTICE);
    this.isFirstMessage = true;
    this.isUserFirstMessage = true;

    this.isGuidedCall = this.isGuided();
    if (this.isGuidedCall && doGuidedValidation) {
      this.isFirstGuidedInteraction = true;
      this.guidedIndex = 0;
      this.callStore.setIsContinueButtonHidden(false);
      this.callStore.setIsContinueButtonBlocked(true);
      await this.getFetchGuidedEchoInfo();
      return;
    }
    this.isStartCallInProgress = true;
    const payloadToSend = this.callStore.getPersonalizedLaunchPayload() || null;

    this.callService
      .startConversation(
        this.controlService.moduleAssignmentUuid,
        this.controlService.callUuid,
        this.controlService.practiceType,
        this.controlService.callType,
        payloadToSend,
        this.callStore.getAcknowledgementSessionToken(), // Pass acknowledgement token
      )
      .subscribe({
        next: async (res: ICallRealtime) => {
          await this.initializeFromStartConversationResponse(res);
          this.isStartCallInProgress = false;
        },
        error: (err) => {
          this.logger.error('[CALL] tavus - start error', err);
          this.isStartCallInProgress = false;

          if (err?.status === 400) {
            console.log(
              '400 error detected in startCallRequest - redirecting to assignment',
            );

            // Add null checks for controlService
            if (this.controlService) {
              this.router.navigate(['assignment'], {
                queryParams: {
                  moduleAssignmentUuid:
                    this.controlService.moduleAssignmentUuid,
                  practiceType: this.controlService.practiceType,
                },
              });
            } else {
              // Fallback if controlService is null
              this.logger.error(
                '[CALL] tavus - Cannot redirect: ControlService is null',
              );
              this.router.navigate(['assignment']);
            }
          } else {
            const error = err instanceof Error ? err : new Error(String(err));
            this._error$.next(error);
          }
        },
      });
  }

  /**
   * Initializes realtime state (practice call, conversation, visual aids, Assembly)
   * from a Tavus/Daily "start conversation" style response.
   * Used both by the generic startConversation flow and by pathway simulation flows.
   */
  async initializeFromStartConversationResponse(
    res: ICallRealtime,
    isGuidedOverride?: boolean,
  ): Promise<void> {
    if (!this.playerService || !this.controlService) {
      return;
    }

    // Determine guided mode; allow caller to override when necessary (e.g. pathway quiz)
    this.isGuidedCall =
      typeof isGuidedOverride === 'boolean'
        ? isGuidedOverride
        : this.isGuided();

    this.playerService.avatarName = res.avatarName;

    // Set practiceCallUuid now that we have it from the response
    this.callStore.setPracticeCallUuid(res.practiceCallUuid);

    // Log call initialization with global properties + additional context
    const language = this.translocoService.getActiveLang();
    this.logger.log('[CALL] tavus - init', {
      language,
      tavusEnabled: true,
      tavusMode: this.isGuidedCall ? 'guided' : 'conversational',
      visualAidEnabled: this.controlService.isVisualAidCall || false,
      pauseEnabled: this.controlService.pauseEnabled || false,
    });

    // Log Tavus start
    this.logger.log('[CALL] tavus - start', {
      pronounceWords: res?.pronounceWords || [],
      resumeMessage: res.resumeMessage || '',
      customBackgroundUrl: res.customBackgroundUrl || '',
    });

    this.callStore.setCall({
      moduleAssignmentUuid: this.controlService.moduleAssignmentUuid,
      callUuid: this.controlService.callUuid,
      callName: res.callName,
      callType: this.controlService.callType,
      advanceType: this.controlService.advanceTypeQueryParam || 'manual',
      callGuidedClips: [],
      practiceCallUuid: res.practiceCallUuid,
      visualAid: this.visualAidObject ?? res.visualAid,
      hideResultsForLearners: res.hideResultsForLearners,
    });
    this.conversationUrl = res.conversationUrl;
    this.conversationId = res.conversationId;
    // Set conversationId as file-level context for all logger calls in this service
    this.logger.setFileContext({ conversationId: this.conversationId });
    this.handleResumeMessage(res.resumeMessage);
    this.controlService.practiceCallUuid = res.practiceCallUuid;
    this.customBackgroundUrl = res.customBackgroundUrl;
    this.callStore.setPronounceWords(res?.pronounceWords || []);
    // Reset first-message flag so handleAvatarStoppedSpeaking starts audio on the
    // new call's first avatar message, even when the previous call ended without
    // going through endRealtimeConversation (e.g. QUIZ auto-complete path).
    this.isFirstMessage = true;
    this.isUserFirstMessage = true;
    await this.handleCallEvents();
    this.startAvatarLoading();
    // if guided call, start the transcriber connection here to prevent delay
    if (this.isGuidedCall) {
      await this.assemblyService.init();
      await this.assemblyService.startTranscriber(false);
    }
  }

  async startFramingCallRequest(enrollmentStepUuid: string): Promise<void> {
    if (!this.controlService) return;
    if (!this.controlService.moduleAssignmentUuid) {
      return;
    }
    if (!this.controlService.callUuid) {
      return;
    }
    if (!this.controlService.practiceType) {
      return;
    }
    if (!this.controlService.callType) {
      return;
    }
    if (!enrollmentStepUuid.trim()) {
      this.logger.error('[CALL] tavus - enrollmentStepUuid is empty');
      return;
    }

    this.controlService.isCaptionsOn = false;
    this.callStore.setDisplayPostCall(false);
    this.callStore.setIsContinueButtonBlocked(true);
    this.callStore.setCurrentView(AppConstants.VIEW_PRACTICE);
    this.isFirstMessage = true;
    this.isUserFirstMessage = true;

    this.isGuidedCall = false; // Framing calls are not guided calls
    this.isStartCallInProgress = true;

    this.callService
      .initiateAndStartFramingCall(
        this.controlService.moduleAssignmentUuid,
        this.controlService.callUuid,
        this.controlService.practiceType,
        this.controlService.callType,
        enrollmentStepUuid,
      )
      .subscribe({
        next: async (res: ICallRealtime) => {
          if (this.playerService && this.controlService) {
            this.playerService.avatarName = res.avatarName;

            // Set practiceCallUuid now that we have it from the response
            this.callStore.setPracticeCallUuid(res.practiceCallUuid);

            // Log call initialization with global properties + additional context
            const language = this.translocoService.getActiveLang();
            this.logger.log('[CALL] tavus - init framing', {
              language,
              tavusEnabled: true,
              tavusMode: 'framing',
              visualAidEnabled: this.controlService.isVisualAidCall || false,
              pauseEnabled: this.controlService.pauseEnabled || false,
            });

            // Log Tavus start
            this.logger.log('[CALL] tavus - start framing', {
              pronounceWords: res?.pronounceWords || [],
              resumeMessage: res.resumeMessage || '',
              customBackgroundUrl: res.customBackgroundUrl || '',
            });

            this.callStore.setCall({
              moduleAssignmentUuid: this.controlService.moduleAssignmentUuid,
              callUuid: this.controlService.callUuid,
              callName: res.callName,
              callType: this.controlService.callType,
              advanceType:
                this.controlService.advanceTypeQueryParam || 'manual',
              callGuidedClips: [],
              practiceCallUuid: res.practiceCallUuid,
              visualAid: this.visualAidObject,
              hideResultsForLearners: res.hideResultsForLearners,
            });
            this.conversationUrl = res.conversationUrl;
            this.conversationId = res.conversationId;
            // Set conversationId as file-level context for all logger calls in this service
            this.logger.setFileContext({ conversationId: this.conversationId });
            this.handleResumeMessage(res.resumeMessage);
            this.controlService.practiceCallUuid = res.practiceCallUuid;
            this.customBackgroundUrl = res.customBackgroundUrl;
            this.callStore.setPronounceWords(res?.pronounceWords || []);
            this.handleCallEvents();
            this.startAvatarLoading();
          }
          this.isStartCallInProgress = false;
        },
        error: (err) => {
          this.logger.error('[CALL] tavus - start framing error', err);
          this.isStartCallInProgress = false;

          if (err?.status === 400) {
            console.log(
              '400 error detected in startFramingCallRequest - redirecting to assignment',
            );

            // Add null checks for controlService
            if (this.controlService) {
              this.router.navigate(['/learning-pathway'], {
                queryParams: {
                  moduleAssignmentUuid:
                    this.controlService.moduleAssignmentUuid,
                  practiceType: AppConstants.TYPE_PATHWAY,
                },
              });
            } else {
              // Fallback if controlService is null
              this.logger.error(
                '[CALL] tavus - Cannot redirect: ControlService is null',
              );
              this.router.navigate(['assignment']);
            }
          } else {
            const error = err instanceof Error ? err : new Error(String(err));
            this._error$.next(error);
          }
        },
      });
  }

  handleResumeMessage(message: string | string[]): void {
    // Define a default "welcome back" message in case no message is provided.
    const defaultResume = ['Hey, welcome back. Ready to continue?'];

    // Early return if message is null or undefined
    if (!message) {
      this.resumeMessage = defaultResume;
      console.log(
        `TAVUS ::: Resume messages: [${this.resumeMessage.join(' | ')}]`,
      );
      return;
    }

    let resumeMsgArray: string[];
    // Handle if message is an array
    if (Array.isArray(message)) {
      // Filter out empty strings and whitespace-only strings
      resumeMsgArray = message
        .filter((msg) => msg && msg.trim() !== '')
        .map((msg) => msg.trim());
    }

    // Handle if message is a string
    else if (typeof message === 'string') {
      // Check if string is empty or only whitespace
      if (message.trim() === '') {
        resumeMsgArray = [];
      } else {
        resumeMsgArray = [message.trim()];
      }
    }
    // Fallback for unexpected types
    else {
      resumeMsgArray = [];
    }

    // If after processing we have no valid messages, use default
    this.resumeMessage =
      resumeMsgArray.length > 0 ? resumeMsgArray : defaultResume;

    console.log(
      `TAVUS ::: Resume messages: [${this.resumeMessage.join(' | ')}]`,
    );
  }

  startAvatarLoading(): void {
    if (!this.playerService) return;
    // Hide loading screen and display avatar loading indicator
    this.playerService.showLoadingScreen = false;
    this.playerService.canPlayVideo = true;
    this.callStore.setIsAvatarThinking(true);

    // Display user video
    setTimeout(() => {
      this.userVideoService.displayUserVideo();
    }, 100);
  }

  canSendUserResponseAndEndCall(): boolean {
    console.log(`TAVUS ::: canSendUserResponseAndEndCall`);
    this.triggerEndCallAfterAnswer = false;
    // Validate if is guided call
    const isGuidedCall = this.isGuided();
    if (!isGuidedCall) return false;

    // Validate if guided clips are available
    if (!this.guidedClips || this.guidedClips.length === 0) return false;

    // Check if any of the remaining guided clips contain a questionUuid
    const isNextQuestionAvailable = this.hasNextQuestion();
    if (isNextQuestionAvailable) return false;

    // Get current questionUuid
    const questionUuid = this.guidedClips[this.guidedIndex].questionUuid;
    if (questionUuid) {
      const isTranscript =
        this.callStore.getSpeechTranscript().length > 0 ||
        this.callStore.getRecognizedTranscripts().length > 0 ||
        this.callStore.getSpeechTranscriptPartial().length > 0;
      // If there is a questionUuid and the user has provided a transcript, we process the user response
      if (isTranscript) {
        this.triggerEndCallAfterAnswer = true;
        console.log(
          `TAVUS ::: Guided call, processing user response for questionUuid [${questionUuid}]`,
        );
        this.callStore.setDisableEndCallButton(true);
        console.log(
          `TAVUS ::: canSendUserResponseAndEndCall, processing user response...`,
        );
        this.processUserResponse(questionUuid);
        this.guidedIndex = this.guidedIndex + 1;
        return true;
      }
    }
    return false;
  }

  cleanupCallResources(): void {
    if (this.dailyCall) {
      this.dailyCall.setLocalVideo(false);
      this.dailyCall.destroy();
      this.dailyCall = null;
      this.joinedMeeting = false;
    }
    this.stopIdleTimer();
    this.assemblyService.stop();
  }

  sendEndCallRequest(): void {
    this.controlService?.sendRequestToEndCall();
  }

  async terminateConversationAsync(): Promise<void> {
    if (!this.controlService) return;

    return new Promise<void>((resolve, reject) => {
      this.callService
        .terminateConversation(
          this.conversationId,
          this.controlService!.practiceCallUuid,
          true,
        )
        .subscribe({
          next: () => {},
          error: (err) => {
            this.logger.error(
              '[CALL] tavus - terminateConversationAsync error',
              err,
            );
            reject(err);
          },
          complete: () => {
            this.callStore.setDisableEndCallButton(false);
            resolve();
          },
        });
    });
  }

  endRealtimeConversation(
    shouldForceEndCall: boolean = false,
    isPathwayFlow: boolean = false,
  ): void {
    const isGuidedCall = this.isGuided();
    const sendUserResponse = this.canSendUserResponseAndEndCall();
    this.logger.log('[CALL] tavus - End realtime conversation', {
      shouldForceEndCall,
      isGuidedCall,
      sendUserResponse,
    });
    if (sendUserResponse) return;

    if (this.playerService && this.controlService) {
      const isLongPause = this.isLongPause;
      this.callStore.setDisableEndCallButton(true);
      // Check if call is complete
      let isComplete = false;
      if (isGuidedCall) {
        this.verifyLastQuestionInGuidedCall();
        // For guided calls, we consider the call complete if the user has answered all questions
        isComplete = this.callStore.getIsCallComplete();
        if (!isComplete && !isLongPause) {
          this.pauseCall();
        }
      } else {
        console.log(
          'TAVUS ::: interaction count:',
          this.controlService.interactionCount,
        );
        isComplete =
          this.controlService.interactionCount >=
          this.controlService.interactionRequirement;

        this.callStore.setIsCallComplete(isComplete);
      }

      console.log(
        `TAVUS ::: Call is complete: ${isComplete}, shouldForceEndCall: ${shouldForceEndCall}`,
      );

      // Display post call screen if call is not complete and mute mic
      if (!isComplete && !shouldForceEndCall) {
        console.log(
          'TAVUS ::: Call is not complete, displaying end call modal',
        );
        this.controlService.isEndCallTriggered = true;
        this.callStore.setDisplayPostCall(false);
        this.toggleMic(false);
        return;
      }

      if (this.dailyCall) {
        this.dailyCall.setLocalVideo(false);
        this.dailyCall.destroy();
        this.dailyCall = null;
        this.joinedMeeting = false;
      }

      this.mediaStreamService.cleanupVideoStream();

      // Clear file-level context when conversation ends
      this.logger.clearFileContext('conversationId');

      // Close the Sentry call-session transaction — must happen before sendRequestToEndCall
      // so the end-call HTTP request is still captured inside the session span.
      // For guided calls sendRequestToEndCall closes the session itself (it calls endCallSession
      // first), so we only close here for non-guided paths.
      if (!isGuidedCall || !isComplete) {
        this.sentryService.endCallSession();
      }

      console.log('TAVUS ::: Call destroyed, stopping idle timer');
      this.stopIdleTimer();
      if (isGuidedCall && isComplete) {
        this.controlService.sendRequestToEndCall();
      }

      this.callStore.setIsPaused(false);
      this.controlService.isEndCallTriggered = false;

      this.callStore.setDisplayPostCall(true);
      this.callStore.setIsAvatarSpeaking(false);
      this.isFirstMessage = true;
      this.conversationInitializedByPathwayFlow = false;

      // Sending last interaction block
      this.handleSendInteraction();

      // Call terminate conversation if is guided call
      if (isGuidedCall || this.isFraming()) {
        console.log('TAVUS ::: Guided call, terminating conversation...');
        // Reset processing state when ending guided call
        if (this.controlService?.isVisualAidCall) {
          this.callStore.setIsProcessingAnswer(false);
        }
        this.assemblyService.stop();
        this.callService
          .terminateConversation(
            this.conversationId,
            this.controlService.practiceCallUuid,
            isPathwayFlow,
          )
          .subscribe({
            next: (res: any) => {
              console.log(res);
            },
            error: (err) => {
              this.logger.error(`[CALL] tavus - ${err}`);
            },
            complete: () => {
              this.callStore.setDisableEndCallButton(false);
            },
          });
      } else {
        if (this.controlService?.isVisualAidCall) {
          this.callStore.setIsProcessingAnswer(false);
        }
        this.callService
          .endConversation(
            this.conversationId,
            this.controlService.practiceCallUuid,
            isPathwayFlow,
          )
          .subscribe({
            next: (res: any) => {
              console.log(res);
            },
            error: (err) => {
              this.logger.error(`[CALL] tavus - ${err}`);
            },
            complete: () => {
              this.callStore.setDisableEndCallButton(false);
            },
          });
      }
      // For pathway step transitions, preserve the user's explicit mic preference
      // across the reset so it is restored when the next step begins.
      const isPathwayStepTransition =
        this.callStore.getModuleAssignmentType() ===
        PathwayConstants.PRACTICE_TYPE_PATHWAY;
      const savedMicPreference = isPathwayStepTransition
        ? this.callStore.getUserMicPreference()
        : undefined;
      this.controlService.resetRecognitionControls();
      if (savedMicPreference !== undefined) {
        this.callStore.setUserMicPreference(savedMicPreference);
        // Also restore isMicOn so the mic button reflects the user's preference immediately
        this.callStore.setIsMicOn(savedMicPreference);
      }
    }
  }

  cancelEndCallEarly(): void {
    if (this.controlService) {
      const isGuidedCall = this.isGuided();
      if (isGuidedCall) {
        this.callStore.setIsResumeFromModal(true);
        this.callStore.setResumeFromModalType('end-call-early');
        this.unpauseCall();
        return;
      }
      this.toggleMic(true);
    }
  }

  togglePause(): void {
    if (!this.controlService) return;
    const isPaused = this.callStore.getIsPaused();
    // unpause call simulation
    if (isPaused) {
      this.unpauseCall();
      return;
    }
    // pause call simulation
    if (!isPaused) {
      this.pauseCall();
    }
  }

  pauseCall(): void {
    if (!this.controlService) return;
    console.log('TAVUS ::: Pausing call');
    // Reset processing state when pausing call
    if (this.controlService?.isVisualAidCall) {
      this.callStore.setIsProcessingAnswer(false);
    }
    const isGuidedCall = this.isGuided();
    if (isGuidedCall) {
      this.assemblyService.pause();
    }
    this.stopIdleTimer();
    this.stopPauseInterval();
    this.stopKeepAliveTimer();
    this.sendConversationInterruptEvent();
    this.setAvatarAudioMuted(true);
    this.toggleUserMediaStream(false);
    this.startPauseInterval();

    this.callStore.setIsPaused(true);
  }

  unpauseCall(): void {
    if (!this.controlService) return;
    const isLongPause = this.isLongPause;
    const isLongPauseInProgress = this.isTeminateConversationInProgress;
    console.log(
      `TAVUS ::: Unpausing call, isLongPause: ${isLongPause}, isLongPauseInProgress: ${isLongPauseInProgress}`,
    );

    this.stopPauseInterval();
    this.controlService.isResumeInProgress = true;
    this.controlService.isResumeMessageInProgress = true;
    if (isLongPauseInProgress) {
      console.log(
        'TAVUS ::: Long pause in progress, not resuming conversation',
      );
      return;
    }

    const resumeMessage = this.randomizeResumeMessage();
    const isGuidedCall = this.isGuided();
    const wasOnModal = this.callStore.getIsResumeFromModal();

    // Default resume message
    this.avatarResumeMessage = resumeMessage;

    // If call is not guided and is a long pause, clean the resume message, since it will come from the backend
    if (isLongPause && !isGuidedCall) {
      this.avatarResumeMessage = '';
    }

    // Guided calls resume message
    if (isGuidedCall) {
      const repeatQuestion = this.guidedClips[this.guidedIndex]?.agentSays;

      // If user was on long pause
      if (isLongPause) {
        this.avatarResumeMessage = `${resumeMessage} ${repeatQuestion}`;
      }

      if (!isLongPause) {
        if (this.wasAvatarSpeaking) {
          // Avatar turn
          this.avatarResumeMessage = repeatQuestion;
        } else {
          if (this.callStore.getResumeFromModalType() === 'settings') {
            // User's turn - Settings: repeat question
            this.avatarResumeMessage = repeatQuestion;
          } else {
            // User's turn -  no avatar response (silence)
            this.avatarResumeMessage = '';
            this.startKeepAliveTimer();

            if (this.controlService?.isMicOnBeforePause) {
              this.assemblyService.resume();
            }
          }
        }
      }
    }

    if (wasOnModal) {
      this.callStore.setIsResumeFromModal(false);
      this.callStore.setResumeFromModalType(null);
    }

    this.lastAvatarSpeech = '';
    this.addChatHistory('agentSays', this.avatarResumeMessage);
    console.log(
      `TAVUS ::: Unpausing call, resume message [${this.avatarResumeMessage}]`,
    );

    if (isLongPause) {
      this.resumeLongPauseConversation();
    } else {
      if (this.avatarResumeMessage.trim().length > 0) {
        this.isResumeMessageInProgress = true;
        this.sendAvatarEchoMessage(this.avatarResumeMessage);
      }
      this.setAvatarAudioMuted(false);
      this.toggleUserMediaStream(true);
      this.controlService.isResumeInProgress = false;
      this.callStore.setIsPaused(false);
    }
  }

  randomizeResumeMessage(): string {
    const messages = this.resumeMessage;
    // Randomly select a message from the array
    const randomIndex = Math.floor(Math.random() * messages.length);
    const randomMessage = messages[randomIndex];
    return randomMessage;
  }

  stopPauseInterval(): void {
    clearInterval(this.pauseInterval);
    this.isLongPause = false;
  }

  async toggleUserMediaStream(turnOn: boolean): Promise<void> {
    this.logger.log(
      `[CALL] tavus - Toggling user media stream: ${turnOn ? 'ON' : 'OFF'}`,
    );
    if (!this.controlService || !this.dailyCall) return;

    const { isVideoOn, isMicOnBeforePause, isVideoOnBeforePause } =
      this.controlService;
    const isMicOn = this.callStore.getIsMicOn();
    if (turnOn) {
      this.callStore.setIsMicOn(isMicOnBeforePause);
      this.controlService.isVideoOn = isVideoOnBeforePause;
      const isResume = this.controlService.isResumeInProgress;
      const setAudioOn = !isResume && isMicOnBeforePause;
      this.dailyCall.setLocalAudio(setAudioOn);
      this.dailyCall.setLocalVideo(isVideoOnBeforePause);

      if (isVideoOnBeforePause) {
        this.userVideoService?.displayUserVideo();
      }

      if (isMicOnBeforePause) {
        this.deviceSettingsService?.startAudioLevelDetection();
      }
    } else {
      this.controlService.isMicOnBeforePause = isMicOn;
      this.controlService.isVideoOnBeforePause = isVideoOn;
      this.callStore.setIsMicOn(false);
      this.controlService.isVideoOn = false;
      this.dailyCall.setLocalAudio(false);
      this.dailyCall.setLocalVideo(false);
      this.userVideoService?.stopUserVideo();
      this.deviceSettingsService?.stopAudioLevelDetection();
    }
  }

  stopUserVideo(): void {
    console.log('TAVUS ::: Stopping user video');
    if (!this.dailyCall) return;

    this.dailyCall.setLocalVideo(false);
    this.userVideoService?.stopUserVideo();
  }

  setAvatarAudioMuted(mute: boolean): void {
    const audioElement = document.getElementById(
      'avatar-audio-realtime',
    ) as HTMLAudioElement | null;

    if (audioElement) {
      audioElement.muted = mute;
    }
  }

  startPauseInterval(): void {
    this.stopPauseInterval();
    this.pauseCountdown = this.LONG_PAUSE_THRESHOLD;
    this.isTeminateConversationInProgress = false;
    this.pauseInterval = setInterval(() => {
      // if call is not active, stop the interval
      if (!this.controlService) return;
      if (!this.playerService) return;
      const isPaused = this.callStore.getIsPaused();
      const isPractice =
        this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
      const isCallComplete = this.callStore.getIsCallComplete();
      if (!isPaused || !isPractice || isCallComplete) {
        this.stopPauseInterval();
        console.log(
          `TAVUS ::: Pause interval stopped, isPaused: ${isPaused}, isPractice: ${isPractice}, isCallComplete: ${isCallComplete}`,
        );
      }
      // decrement the countdown
      this.pauseCountdown -= 1;

      // if the countdown reaches zero, trigger the terminateConversation due to long pause
      if (this.pauseCountdown <= 0) {
        this.terminateConversation();
      }
    }, 1000);
  }

  sendConversationInterruptEvent(): void {
    if (!this.dailyCall) {
      this.logger.error(
        'TAVUS ::: Unable to send interrupt — call session is not initialized.',
      );
      return;
    }

    if (!this.conversationId) {
      this.logger.error(
        '[CALL] tavus - Unable to send interrupt — conversation ID is missing.',
      );
      return;
    }

    if (!this.joinedMeeting) {
      this.logger.error(
        '[CALL] tavus - Unable to send interrupt — user has not joined the meeting yet.',
      );
      return;
    }

    const payload = {
      message_type: 'conversation',
      event_type: 'conversation.interrupt',
      conversation_id: this.conversationId,
    };

    this.dailyCall.sendAppMessage(payload, '*');

    // Log interrupt event sent
    this.logger.log('[CALL] tavus - send interrupt event');
  }

  sendAvatarEchoMessage(text: string): void {
    if (!this.dailyCall) {
      if (this.isStartCallInProgress) return;
      // If call session is not initialized, we start a new call
      this.startCallRequest(false);
      return;
    }

    if (!this.conversationId) {
      return;
    }

    if (!this.joinedMeeting) {
      setTimeout(() => {
        this.sendAvatarEchoMessage(text);
      }, 2000); // Retry after 2 seconds
      return;
    }

    if (text.trim() === '') {
      return;
    }

    const speech = this.getAvatarCorrectPronunciation(text);

    const payload = {
      message_type: 'conversation',
      event_type: 'conversation.echo',
      conversation_id: this.conversationId,
      properties: {
        modality: 'text',
        text: speech,
        done: true,
      },
    };

    this.dailyCall.sendAppMessage(payload, '*');

    // Log echo message sent
    this.logger.log('[CALL] tavus - send echo message', {
      message: speech,
    });

    if (!this.playerService) return;
    this.playerService.avatarSpeech.set(text);
  }

  getAvatarCorrectPronunciation(text: string): string {
    /*
      Replace mispronounced words with their correct pronunciation for avatar speech.
      pronounceWords
        "to" = correct pronunciation ("nov var tis")
        "from" = correct text ("Novartis")
     */
    return replaceFromWithTo(text, this.callStore.getPronounceWords());
  }

  getAvatarCorrectText(text: string): string {
    /*
      Replace correctly pronounced words with correct text for avatar closed captions.
        "to" = correct pronunciation ("nov var tis")
        "from" = correct text ("Novartis")
     */
    return replaceToWithFrom(text, this.callStore.getPronounceWords());
  }

  terminateConversation(): void {
    if (!this.controlService) return;

    clearInterval(this.pauseInterval);
    this.isTeminateConversationInProgress = true;
    this.isLongPause = true;

    console.log(
      'TAVUS ::: Pause countdown reached zero. Triggering terminateConversation.',
    );
    this.callService
      .terminateConversation(
        this.conversationId,
        this.controlService.practiceCallUuid,
      )
      .subscribe({
        next: (res: any) => {
          console.log(`TAVUS ::: Terminate conversation response:`, res);
        },
        error: (err) => {
          this.logger.error(
            `[CALL] tavus - Error terminating conversation:`,
            err,
          );
          this.isTeminateConversationInProgress = false;
          if (this.controlService?.isResumeInProgress) {
            this.resumeLongPauseConversation();
          }
        },
        complete: () => {
          this.isTeminateConversationInProgress = false;
          if (this.controlService?.isResumeInProgress) {
            console.log(
              'TAVUS ::: Triggering resume conversation after termination.',
            );
            this.resumeLongPauseConversation();
          }
        },
      });
  }

  async resumeLongPauseConversation(): Promise<void> {
    if (!this.controlService) return;
    console.log('TAVUS ::: Resuming conversation...');

    await this.callService
      .resumeConversation(
        this.conversationId,
        this.controlService.practiceCallUuid,
      )
      .subscribe({
        next: async (res: any) => {
          console.log(`TAVUS ::: Resume conversation response:`, res);
          console.log(
            `TAVUS ::: Resuming conversation with ID: ${res.conversationId}`,
          );
          console.log(
            `TAVUS ::: Resuming conversation with URL: ${res.conversationUrl}`,
          );
          this.conversationId = res.conversationId;
          this.conversationUrl = res.conversationUrl;
          // Update file-level context with new conversationId
          this.logger.setFileContext({ conversationId: this.conversationId });
          // If backend returns no message, fall back to default so avatar speaks and
          // handleConversationStarted() fires to clear the pause modal.
          if (!this.isGuided() && !this.avatarResumeMessage?.trim()) {
            this.avatarResumeMessage = this.randomizeResumeMessage() || '';
          }
          this.setAvatarAudioMuted(false);
          this.triggerResumeMessage = true;
          await this.handleCallEvents();
        },
        error: (err) => {
          this.logger.error(`[CALL] tavus - Error resuming conversation:`, err);
          this.controlService!.isResumeInProgress = false;
          this.controlService!.isResumeMessageInProgress = false;
        },
      });
  }

  getFetchGuidedEchoInfo(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.controlService) {
        reject();
        return;
      }
      const { moduleAssignmentUuid, callUuid } = this.controlService;
      if (!moduleAssignmentUuid) {
        this.logger.error('[CALL] tavus - module assignment id null - error', {
          callWords: this.callStore.getCallWords(),
          replaceWords: this.callStore.getReplaceWords(),
        });
        reject();
        return;
      }

      if (!callUuid) {
        this.logger.error(
          '[CALL] tavus - callUuid is null to fetch guided info',
        );
        reject();
        return;
      }

      this.callService
        .fetchGuidedEchoInfo(moduleAssignmentUuid, callUuid)
        .subscribe({
          next: (res: IGuidedInfo) => {
            console.log('TAVUS ::: Guided echo info:', res);
            this.logger.log('[CALL] tavus - fetchGuidedEchoInfo success', {
              callWords: res?.callWords || [],
              replaceWords: res?.replaceWords || [],
            });
            this.guidedInfo = res;
            this.guidedClips = res.callGuidedSteps;
            this.callStore.setReplaceWords(res?.replaceWords || []);
            this.callStore.setIsCallComplete(false);
            this.callStore.setCallWords(res?.callWords || []);
            this.callStore.setSttWords(res?.sttWords || []);
            this.callStore.setIsContinueButtonBlocked(true);
            const existingCall = this.callStore.getCall();
            this.callStore.setCall({
              moduleAssignmentUuid: moduleAssignmentUuid,
              callUuid: callUuid,
              callName: res.callName,
              callType: res.callType,
              advanceType: res.advanceType,
              callGuidedClips: [],
              practiceCallUuid: existingCall?.practiceCallUuid || '',
              visualAid: res?.visualAid,
              hideResultsForLearners: existingCall?.hideResultsForLearners,
            });

            this.visualAidObject = res?.visualAid;
            // Set first hint
            if (this.guidedClips.length > 0) {
              const hints = this.guidedClips[0].hints || '';
              this.callStore.setHints(hints);
            }

            const baseUrl = res?.visualAid?.baseUrl || '';
            if (baseUrl !== '') {
              // Get visual aid indices from call steps
              const guidedSteps = res?.callGuidedSteps || [];
              const indices =
                this.imagePreloaderService.getVisualAidIndices(guidedSteps);

              if (indices.length === 0) return;
              // Preload all images for the visual aid call
              console.log('Unique Visual Aid Indices:', indices);
              this.imagePreloaderService
                .preloadImages(baseUrl, indices)
                .then((results: IPreloadedImageResult[]) => {
                  this.callStore.setVisualAidImages(results);
                  console.log(
                    `Successfully preloaded ${results.length} images`,
                  );
                  // Set the first image URL for display
                  if (results.length > 0) {
                    const firstImage = results[0].src;
                    console.log('First preloaded image URL:', firstImage);
                    this.callStore.setVisualAidImageUrl(firstImage);
                  }
                })
                .catch((error) => {
                  this.logger.error(
                    '[CALL] tavus - Error preloading images:',
                    error,
                  );
                });
            }

            // Count total questions in guided call
            const totalQuestions = this.guidedClips.filter(
              (clip) =>
                clip.questionUuid != null && clip.questionUuid.trim() !== '',
            ).length;
            console.log(
              `TAVUS ::: Total questions in guided call: ${totalQuestions}`,
            );

            if (!this.controlService) {
              reject();
              return;
            }
            if (!this.playerService) {
              reject();
              return;
            }
            this.controlService.interactionRequirement = totalQuestions;

            const displayTour = this.shouldDisplayTour();
            if (displayTour) {
              this.playerService.showLoadingScreen = false;
              this.controlService.displayTour = true;
              this.callStore.setIsAvatarThinking(true);
            } else if (!this.conversationInitializedByPathwayFlow) {
              this.startCallRequest(false);
            }
            resolve();
          },
          error: (err) => {
            this.logger.error(
              '[CALL] tavus - Error fetching guided echo info:',
              err,
            );
            reject(err);
          },
        });
    });
  }

  addChatHistory(
    speaker: string,
    speech: string,
    forceEmpty: boolean = false,
  ): void {
    if ((!speech || speech.trim() === '') && !forceEmpty) return;

    let convoHistory: { chatHistory: string[] } = {
      chatHistory: [],
    };

    if (localStorage.getItem('convoHistory')) {
      convoHistory = JSON.parse(localStorage.getItem('convoHistory') || '') as {
        chatHistory: string[];
      };
    }

    // Prevent duplicate entry at the last position
    const isDuplicatedAtPosition1 = this.isAgentSpeechRepeated(
      speaker,
      speech,
      1,
      convoHistory,
    );
    if (isDuplicatedAtPosition1 && !forceEmpty) return;

    // Prevent duplicate entry at the second to last position
    const isDuplicatedAtPosition2 = this.isAgentSpeechRepeated(
      speaker,
      speech,
      2,
      convoHistory,
    );
    if (isDuplicatedAtPosition2 && !forceEmpty) return;

    convoHistory.chatHistory.push(`${speaker}: ${speech}`);
    localStorage.setItem('convoHistory', JSON.stringify(convoHistory));

    // Log chat history
    const chatLines = convoHistory.chatHistory.map((entry, index) => {
      const [speaker, ...speechParts] = entry.split(':');
      const speech = speechParts.join(':').trim();
      const icon = speaker.trim() === 'agentSays' ? '🤖' : '💬';
      const line = `${icon} ${speaker.trim()}: ${speech || '(empty)'}`;

      // Highlight the last input in bold
      return index === convoHistory.chatHistory.length - 1 ? `%c${line}` : line;
    });

    console.log(
      `TAVUS ::: Chat History Updated:\n${chatLines.join('\n')}`,
      'font-weight: bold;',
    );
  }

  isAgentSpeechRepeated(
    speaker: string,
    speech: string,
    stepsBack: number,
    conversationHistory: { chatHistory: string[] },
  ): boolean {
    // Only check for agent duplicates
    if (speaker !== 'agentSays') return false;

    // Ensure we can look back that many steps
    const historyLength = conversationHistory.chatHistory.length;
    if (stepsBack < 1 || historyLength < stepsBack) {
      return false;
    }

    const previousEntry =
      conversationHistory.chatHistory[historyLength - stepsBack];
    const currentEntry = `${speaker}: ${speech}`;

    return previousEntry === currentEntry;
  }

  shouldDisplayTour(): boolean {
    if (!this.controlService) return false;

    // Check if is guided call
    const isGuidedCall = this.isGuided();
    if (!isGuidedCall) return false;

    // Check call UUID
    const uuid = this.callStore.getCall()?.callUuid;
    if (!uuid) return false;

    // Check if call is visual aid, for visual aid calls, do not display tour
    if (this.controlService.isVisualAidCall) return false;

    if (!this.controlService.hasAccessedCall(uuid)) {
      this.controlService.storeCallUUID(uuid);
      console.log('TAVUS ::: Call accessed for the first time, display tour.');
      return true;
    } else {
      console.log(
        'TAVUS ::: Call has already been accessed, do not display tour.',
      );
    }
    return false;
  }

  async resetAssemblyAudio(
    audioDeviceSelected: string,
    startNewRecording: boolean,
  ): Promise<void> {
    this.logger.log(
      '[CALL] tavus - reset assembly audio with selected device',
      {
        audioDeviceSelected,
        startNewRecording,
      },
    );
    this.triggerStartTranscriber = startNewRecording;

    // restartTranscriber will be called on toggleMic
  }

  handleAvatarEmptySpeech() {
    if (this.guidedIndex < this.guidedClips.length - 1) {
      const clip = this.guidedClips[this.guidedIndex];
      const isNextUserStep =
        clip && (!clip.agentSays || !clip.agentSays.trim());
      if (isNextUserStep && this.callStore.getIsMicOn()) {
        const isCallActive = this.callValidationService.isCallActive();
        if (!isCallActive) {
          this.logger.log(
            '[CALL] tavus - User is not in an active call — avatar empty speech handling canceled.',
          );
          return;
        }
        this.logger.log('[CALL] tavus - handle empty avatar speech');
        this.assemblyService.connectTranscriber(true);
        this.startKeepAliveTimer();
      } else {
        this.shouldConnectTranscriberOnMicOn = true;
        console.log(
          'Mic is off, will connect transcriber when mic is turned on.',
        );
      }
    } else {
      // End of guided steps, do not restart transcriber
      console.log('End of guided steps reached, not restarting transcriber.');
    }
  }

  private readonly CHROMA_VERTEX_SHADER = `
    attribute vec2 a_position;
    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;
    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
      v_texCoord = a_texCoord;
    }
  `;

  private readonly CHROMA_FRAGMENT_SHADER = `
    precision mediump float;
    uniform sampler2D u_video;
    uniform sampler2D u_background;
    uniform vec3 u_chromaColor;
    uniform float u_threshold;
    uniform float u_smoothness;
    uniform float u_spill;
    varying vec2 v_texCoord;

    vec3 rgb2yuv(vec3 rgb) {
      return vec3(
        0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b,
        -0.14713 * rgb.r - 0.28886 * rgb.g + 0.436 * rgb.b,
        0.615 * rgb.r - 0.51499 * rgb.g - 0.10001 * rgb.b
      );
    }

    void main() {
      vec4 videoColor = texture2D(u_video, v_texCoord);
      vec4 bgColor = texture2D(u_background, v_texCoord);

      vec3 videoYuv = rgb2yuv(videoColor.rgb);
      vec3 chromaYuv = rgb2yuv(u_chromaColor);

      float distance = length(videoYuv.yz - chromaYuv.yz);
      float alpha = smoothstep(u_threshold - u_smoothness, u_threshold + u_smoothness, distance);

      // Spill suppression
      vec3 spillSuppressed = videoColor.rgb;
      if (alpha < 1.0) {
        float spillAmount = max(0.0, u_chromaColor.g - max(videoColor.r, videoColor.b)) * u_spill;
        spillSuppressed.g -= spillAmount;
      }

      vec3 result = mix(bgColor.rgb, spillSuppressed, alpha);
      gl_FragColor = vec4(result, 1.0);
    }
  `;

  startChromaKeying(
    videoElement: HTMLVideoElement,
    canvasElement: HTMLCanvasElement,
    backgroundImagePath: string,
    options: { threshold?: number; smoothness?: number; spill?: number; chromaColor?: [number, number, number] } = {}
  ) {
    const {
      threshold = 0.25,
      smoothness = 0.25,
      spill = 0.3,
      chromaColor = [0, 255, 155],
    } = options;

    if (videoElement) {
      videoElement.crossOrigin = 'anonymous';
    }

    const gl = canvasElement.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) {
      this.logger.error('[CALL] tavus - [ChromaKey] WebGL not supported');
      return;
    }

    const program = this.createChromaShaderProgram(gl);
    if (!program) return;

    // Set up geometry (full-screen quad)
    const positions = [-1, -1, 1, -1, -1, 1, 1, 1];
    const texCoords = [0, 1, 1, 1, 0, 0, 1, 0];

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);

    const positionAttr = gl.getAttribLocation(program, 'a_position');
    const texCoordAttr = gl.getAttribLocation(program, 'a_texCoord');
    const videoUniform = gl.getUniformLocation(program, 'u_video');
    const bgUniform = gl.getUniformLocation(program, 'u_background');
    const chromaColorUniform = gl.getUniformLocation(program, 'u_chromaColor');
    const thresholdUniform = gl.getUniformLocation(program, 'u_threshold');
    const smoothnessUniform = gl.getUniformLocation(program, 'u_smoothness');
    const spillUniform = gl.getUniformLocation(program, 'u_spill');

    const videoTexture = gl.createTexture();
    const bgTexture = gl.createTexture();

    let bgImg: HTMLImageElement | null = null;
    let bgImgLoaded = false;

    const loadBgImage = (path: string) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = path;
      img.onload = () => {
        bgImg = img;
        bgImgLoaded = true;
      };
      img.onerror = () => {
        this.logger.error(
          '[CALL] tavus - [ChromaKey] Failed to load background image:',
          path,
        );
      };
    };
    loadBgImage(this.getDynamicBgPath(backgroundImagePath));

    const render = () => {
      if (!videoElement.videoWidth || !videoElement.videoHeight || !bgImgLoaded || !bgImg) {
        this.chromaKeyAnimationFrameId = requestAnimationFrame(render);
        return;
      }

      // Sync canvas size to video
      if (canvasElement.width !== videoElement.videoWidth || canvasElement.height !== videoElement.videoHeight) {
        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        gl.viewport(0, 0, canvasElement.width, canvasElement.height);
      }

      gl.useProgram(program);

      // Video texture
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, videoTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, videoElement);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

      // Background texture
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, bgTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bgImg);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

      // Uniforms
      gl.uniform1i(videoUniform, 0);
      gl.uniform1i(bgUniform, 1);
      gl.uniform3f(chromaColorUniform, chromaColor[0] / 255, chromaColor[1] / 255, chromaColor[2] / 255);
      gl.uniform1f(thresholdUniform, threshold);
      gl.uniform1f(smoothnessUniform, smoothness);
      gl.uniform1f(spillUniform, spill);

      // Attributes
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionAttr);
      gl.vertexAttribPointer(positionAttr, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.enableVertexAttribArray(texCoordAttr);
      gl.vertexAttribPointer(texCoordAttr, 2, gl.FLOAT, false, 0, 0);

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      this.chromaKeyAnimationFrameId = requestAnimationFrame(render);
    };

    canvasElement.width = videoElement.videoWidth || 1280;
    canvasElement.height = videoElement.videoHeight || 720;
    gl.viewport(0, 0, canvasElement.width, canvasElement.height);

    this.chromaKeyAnimationFrameId = requestAnimationFrame(render);
  }

  private createChromaShaderProgram(gl: WebGLRenderingContext): WebGLProgram | null {
    const compileShader = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        this.logger.error('[CALL] tavus - [ChromaKey] Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = compileShader(gl.VERTEX_SHADER, this.CHROMA_VERTEX_SHADER);
    const fragmentShader = compileShader(gl.FRAGMENT_SHADER, this.CHROMA_FRAGMENT_SHADER);
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      this.logger.error('[CALL] tavus - [ChromaKey] Shader link error:', gl.getProgramInfoLog(program));
      return null;
    }

    return program;
  }

  private getDynamicBgPath(backgroundImagePath: string): string {
    const separator = backgroundImagePath.includes('?') ? '&' : '?';
    return `${backgroundImagePath}${separator}t=${Date.now()}`;
  }

  stopChromaKeying() {
    if (this.chromaKeyAnimationFrameId) {
      cancelAnimationFrame(this.chromaKeyAnimationFrameId);
      this.chromaKeyAnimationFrameId = null;
    }
  }

  /**
   * Handles incoming transcription messages from Daily.co call participants.
   * Processes transcription events and stores them with participant type labels.
   *
   * @param event - Transcription event object containing text and participantId
   */
  handleTranscriptionMessage(event: any): void {
    // Validate event has required properties
    if (!event || typeof event !== 'object') {
      this.logger.warn('[CALL] tavus - Invalid transcription event received');
      return;
    }

    if (!('text' in event) || !('participantId' in event)) {
      this.logger.warn(
        '[CALL] tavus - Transcription event missing required fields (text or participantId)',
      );
      return;
    }

    const { text, participantId } = event;

    // Validate text is not empty
    if (!text || typeof text !== 'string' || text.trim() === '') {
      this.logger.warn('[CALL] tavus - Transcription text is empty, skipping');
      return;
    }

    const participantType = this.getParticipantType(participantId);

    this.logger.log('[CALL] tavus - transcription message', {
      participantType,
      message: text,
      event,
    });

    if (this.isGuidedCall) return;

    // Handle unknown participant
    if (!participantType) {
      this.logger.warn(
        `[CALL] tavus - Unknown participant ID: ${participantId}, skipping transcription`,
      );
      return;
    }

    const isAgent = participantType === this.participantAgent;
    if (isAgent && this.isFirstMessage) {
      console.log(
        `TAVUS ::: first avatar message omitted in transcription block`,
      );
      this.sendPayload = false;
      return;
    }

    // Prevents sending user's first message
    if (this.isUserFirstMessage) {
      this.isUserFirstMessage = false;
      return;
    }

    // Reset resume message tracking if user speaks (interrupts the avatar resume message)
    if (!isAgent && this.isResumeMessageInProgress) {
      this.isResumeMessageInProgress = false;
      this.avatarResumeAccumulated = '';
    }

    // Check if text is part of avatar resume message and skip to avoid duplicates
    if (this.shouldSkipResumeMessage(isAgent, text)) {
      return;
    }

    this.sendPayload = true;

    const transcript = `${participantType}: ${text}`;
    const currentTranscriptions = this.callStore.getTranscriptions();

    this.callStore.setTranscriptions([...currentTranscriptions, transcript]);

    console.log(
      `TAVUS ::: Full transcriptions array:`,
      this.callStore.getTranscriptions(),
    );
  }

  /**
   * Normalizes text for comparison by converting to lowercase,
   * removing punctuation/special characters, and normalizing whitespace.
   *
   * @param text - The text to normalize
   * @returns Normalized text with lowercase, no punctuation, and single spaces
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Checks if the given text is part of the avatar resume message and should be skipped.
   * Handles resume messages split across multiple transcript chunks by accumulating text.
   *
   * @param isAgent - Whether the message is from the agent/avatar (could be from the user)
   * @param text - The transcript text to check
   * @returns true if the text should be skipped (is part of resume message), false otherwise
   */
  private shouldSkipResumeMessage(isAgent: boolean, text: string): boolean {
    // Only check resume message for agent messages during resume
    if (!isAgent || !this.isResumeMessageInProgress) {
      return false;
    }

    const normalizedTranscript = this.normalizeText(text);
    const normalizedResume = this.normalizeText(this.avatarResumeMessage);

    // Accumulate the text to handle resume messages split across multiple chunks
    this.avatarResumeAccumulated += ' ' + normalizedTranscript;
    this.avatarResumeAccumulated = this.avatarResumeAccumulated.trim();

    // Check if current chunk or accumulated text is part of resume message
    const isPartOfResume =
      normalizedResume.includes(normalizedTranscript) ||
      normalizedResume.includes(this.avatarResumeAccumulated);

    if (isPartOfResume) {
      console.log(
        `TAVUS ::: resume message chunk omitted on the AIB transcript`,
      );

      // Check if we've received the complete resume message
      if (normalizedResume === this.avatarResumeAccumulated) {
        console.log(
          `TAVUS ::: complete resume message received, resetting accumulator`,
        );
        this.isResumeMessageInProgress = false;
        this.avatarResumeAccumulated = '';
      }

      return true;
    } else {
      // Text doesn't match resume message, reset and continue normally
      console.log(
        `TAVUS ::: text doesn't match resume message, resetting accumulator`,
      );
      this.isResumeMessageInProgress = false;
      this.avatarResumeAccumulated = '';
      return false;
    }
  }

  /**
   * Determines the participant type based on their Daily.co session ID.
   *
   * @param participantId - The Daily.co session ID of the participant
   * @returns 'Agent' for avatar/replica, 'User' for local participant, or null if unknown
   */
  getParticipantType(participantId: string): string | null {
    if (!participantId || typeof participantId !== 'string') {
      this.logger.warn(
        '[CALL] tavus - Invalid participantId provided to getParticipantType',
      );
      return null;
    }

    if (participantId === this.replicaIdDaily) {
      return this.participantAgent;
    }

    if (participantId === this.userIdDaily) {
      return this.participantUser;
    }

    this.logger.warn(`[CALL] tavus - Unknown participant ID: ${participantId}`);
    return null;
  }

  /**
   * Sends the agent interaction block to the backend for conversational calls.
   * This includes the agent's complete response and all speech-to-text transcriptions
   * collected during the current interaction segment.
   *
   * The interaction block is sent when the user starts speaking (after the second avatar response)
   * and when the call ends, capturing the full conversation context.
   */
  handleSendInteraction(): void {
    if (this.isGuidedCall) return;
    if (!this.playerService) return;
    if (!this.sendPayload) return;

    // Validate call data exists and contains practice call UUID
    const callData = this.callStore.getCall();
    if (!callData) {
      this.logger.error(
        '[CALL] tavus - Empty call data in handleSendInteraction',
      );
      return;
    }

    const practiceCallUuid = callData.practiceCallUuid;
    if (!practiceCallUuid) {
      this.logger.error(
        '[CALL] tavus - Empty practiceCallUuid in handleSendInteraction',
      );
      return;
    }

    const sttTranscriptions = this.callStore.getTranscriptions();
    this.callStore.setTranscriptions([]);

    // Build the agent interaction block request
    const agentSaysFull = this.avatarLastMessage;
    const interactionBlockRequest: IAgentInteractionBlockRequest = {
      practiceCallUuid,
      agentSaysFull,
      sttTranscriptions,
    };

    this.logger.log(`[CALL] tavus - send AIB`, interactionBlockRequest);

    // Send the interaction block to the backend
    this.callService
      .sendAgentInteractionBlock(interactionBlockRequest)
      .subscribe({
        next: (serverResponse: any) => {
          this.logger.log(
            '[CALL] tavus - response sendAgentInteractionBlock',
            serverResponse,
          );
        },
        error: (errorResponse: any) => {
          this.logger.error(
            '[CALL] tavus - error on sendAgentInteractionBlock',
            errorResponse,
          );
        },
      });
  }
}
